﻿import { EmployeeDataSection } from '@/common/types/employee-data-permission.def.ts'
import React from 'react'
import { Button, Col, Flex, Form, notification, Row, Typography, Upload, UploadFile, UploadProps } from 'antd'
import {
  useBulkImportStaffCostMutation,
  useExportStaffCostDataMutation,
  useExportStaffCostImportTemplateMutation,
} from '@/features/employee-data/employee-data.api.ts'
import { AntdForm } from '@/common/components/AntdForm'
import { AntdFormRules } from '@/common/components/AntdForm/rules.tsx'
import Title from 'antd/es/typography/Title'
import { handleApiError } from '@/utils/api.tsx'
import { VendorCostReportImportForm } from '@/pages/staff-cost/VendorCostReportImportForm.tsx'

type StaffCostImportExportProps = {}

export const StaffCostImportExport: React.FC<StaffCostImportExportProps> = () => {
  return (
    <section>
      <Row gutter={16}>
        <Col span={12}>
          <div className="border p-4 rounded-lg mb-4">
            <VendorCostReportImportForm />
          </div>
        </Col>
        <Col span={12}>
          <div className="border p-4 rounded-lg mb-4">
            <StaffCostImportForm />
          </div>
        </Col>
        <Col span={12}>
          <div className="border p-4 rounded-lg mb-4">
            <StaffCostExportForm />
          </div>
        </Col>
      </Row>
    </section>
  )
}

// @ts-ignore
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StaffCostImportForm: React.FC = () => {
  const [bulkImportStaffCost, { isLoading }] = useBulkImportStaffCostMutation()
  const [downloadTemplate, { isLoading: isDownloadingTemplate }] = useExportStaffCostImportTemplateMutation()

  const uploadButtonProps: UploadProps = {
    maxCount: 1,
    showUploadList: true,
    beforeUpload: () => false,
    multiple: false,
    accept: '.xlsx',
  }

  const onImport = async ({ excelFiles }: { excelFiles: UploadFile[] }) => {
    const excelFile = excelFiles[0]
    const result = await bulkImportStaffCost({
      excelFile: excelFile.originFileObj as unknown as File,
    })
    if ('data' in result) {
      notification.success({ message: 'Import Successfully' })
    } else {
      handleApiError(result.error)
    }
  }

  return (
    <Flex vertical gap="small">
      <Title level={4}>Import New Staff Cost Data</Title>
      <Typography.Text>All the data in the excel will be imported as new data. Existing records will not be
        amended.</Typography.Text>
      <Typography.Text>You are only allowed to import employee data with write access.</Typography.Text>
      <Form layout="vertical" onFinish={onImport}>
        <AntdForm.Item
          name="excelFiles"
          label=""
          rules={[AntdFormRules.required]}
          valuePropName="fileList"
          getValueFromEvent={e => e.fileList}
        >
          <Upload {...uploadButtonProps}>
            <Button htmlType="button">Select Excel File (.xlsx)</Button>
          </Upload>
        </AntdForm.Item>
        <Flex gap="small">
          <Button htmlType="submit" type="primary" loading={isLoading}>
            Import
          </Button>
          <Button htmlType="button" onClick={() => downloadTemplate()} loading={isDownloadingTemplate}>
            Download Template
          </Button>
        </Flex>
      </Form>
    </Flex>
  )
}

type FormValues = {
  year: number,
  month?: number
  employmentEntities: string[],
}

const StaffCostExportForm: React.FC = () => {
  const [exportStaffCost, { isLoading }] = useExportStaffCostDataMutation()

  const onExport = async (data: FormValues) => {
    const result = await exportStaffCost({
      ...data,
    })
    if ('data' in result) {
      notification.success({ message: 'Export Successfully' })
    } else {
      handleApiError(result.error)
    }
  }

  return (
    <Flex vertical gap="small">
      <Title level={4}>Export Staff Cost Data</Title>
      <Form layout="vertical" onFinish={onExport}>
        <AntdForm.Section>
          <AntdForm.Year fieldProps={{ name: 'year', rules: [AntdFormRules.required] }} />
          <AntdForm.Month fieldProps={{ name: 'month' }} />
        </AntdForm.Section>
        <AntdForm.ManagedEmploymentEntities
              fieldProps={{ name: 'employmentEntities', rules: [AntdFormRules.required] }}
              targetSections={[EmployeeDataSection.StaffCost]}
            />
        <Button htmlType="submit" type="primary" loading={isLoading}>
          Export
        </Button>
      </Form>
    </Flex>
  )
}
